/**
 * Colecciones: grupos de ofertas con nombre, descripción e imagen, como las
 * de Shopify («Verano», «Ofertas», «Nike»). Manuales o automáticas por reglas.
 *
 * Fase 1: crearlas y editarlas aquí. Su página en el sitio, los filtros sobre
 * las listas y usarlas como fuente en el editor vienen después.
 */
import { editorHref } from '../../../lib/editorSession'
import { useEffect, useState } from 'react'
import { IconCategory, IconDots, IconExternalLink, IconLayoutGrid, IconLink, IconPencil, IconPlus, IconTrash, IconWand, IconX } from '@tabler/icons-react'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { SectionRefresh } from '../../SectionRefresh'
import { HeaderRefreshButton } from '../ListSelection'
import { ConfirmDialog } from '../ConfirmDialog'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useI18n, translateServerError } from '../../../lib/i18n'
import { useRegion } from '../../../lib/region'
import { useToast } from '../../Toast'
import { CollectionDialog } from './CollectionDialog'
import { deleteCollection, getOfferFields, listCollections, listOffers, type OfferCollection } from '../../../lib/api'

export function CollectionsSection({ projectId, token, siteOrigin }: {
  projectId: string
  token: string
  siteOrigin?: string | undefined
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const { currency } = useRegion()
  const collections = useSectionResource(token, [projectId, 'collections'], () => listCollections(projectId, token))
  const offers = useSectionResource(token, [projectId, 'offers'], () => listOffers(projectId, token))
  const fields = useSectionResource(token, [projectId, 'offer-fields'], () => getOfferFields(projectId, token))
  const [editing, setEditing] = useState<OfferCollection | 'new' | null>(null)
  const [removing, setRemoving] = useState<OfferCollection | null>(null)
  const [menuId, setMenuId] = useState<string | null>(null)
  /* La recién creada: arriba sale cómo diseñar su página en el editor. */
  const [created, setCreated] = useState<OfferCollection | null>(null)
  /* Su página en el editor: la plantilla que comparten todas, con ésta de muestra. */
  const designHref = (collection: OfferCollection) => editorHref(`?pid=${encodeURIComponent(projectId)}&collection=${encodeURIComponent(collection.slug)}`)
  useEffect(() => {
    if (!menuId) return
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Element && !event.target.closest('.dash-collection-menu')) setMenuId(null)
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [menuId])
  const rows = collections.data ?? []
  const offerById = new Map((offers.data ?? []).map((offer) => [offer.id, offer]))

  /* La dirección de su página en el sitio publicado. */
  const collectionUrl = (collection: OfferCollection) => `${(siteOrigin ?? '').replace(/\/$/, '')}/colecciones/${encodeURIComponent(collection.slug)}`
  const copyLink = async (collection: OfferCollection) => {
    try {
      await navigator.clipboard.writeText(collectionUrl(collection))
      showToast(t('collections.linkCopied'))
    } catch {
      showToast(t('collections.linkCopyFailed'), 'error')
    }
  }

  const saved = (collection: OfferCollection) => {
    if (editing === 'new') setCreated(collection)
    collections.setData([...rows.filter((row) => row.id !== collection.id), collection].sort((a, b) => a.sortOrder - b.sortOrder))
    setEditing(null)
    showToast(t('collections.saved'))
  }

  return (
    <div className="dash-projects-view dash-pages-view dash-collections-view dash-loaded-content-in">
      <SectionRefresh refreshing={collections.refreshing} failed={Boolean(collections.error)} />
      <DashboardPageHeader
        title={t('collections.title')}
        subtitle={t('collections.subtitle')}
        actions={(
          <>
            <HeaderRefreshButton refreshing={collections.refreshing} onRefresh={() => { void collections.refresh() }} />
            <button type="button" className="dash-primary-btn" onClick={() => setEditing('new')}>
              <IconPlus size={15} /> {t('collections.new')}
            </button>
          </>
        )}
      />

      {created ? (
        <div className="dash-collection-created" role="status">
          <span className="dash-widget-glass-icon" aria-hidden="true"><IconLayoutGrid size={18} /></span>
          <span>
            <strong>{t('collections.createdTitle', { title: created.title })}</strong>
            <small>{t('collections.createdBody')}</small>
          </span>
          <a className="dash-primary-btn" href={designHref(created)} target="_blank" rel="noreferrer" onClick={() => setCreated(null)}>
            <IconPencil size={14} /> {t('collections.designPage')}
          </a>
          <button type="button" className="dash-icon-btn" aria-label={t('collections.form.close')} onClick={() => setCreated(null)}><IconX size={14} /></button>
        </div>
      ) : null}

      {collections.data && rows.length === 0 ? (
        <div className="dash-placeholder">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconCategory size={20} /></span>
          <strong>{t('collections.emptyTitle')}</strong>
          <p>{t('collections.emptyBody')}</p>
          <button type="button" className="dash-primary-btn" onClick={() => setEditing('new')}><IconPlus size={15} /> {t('collections.new')}</button>
        </div>
      ) : (
        <div className="dash-collections-grid">
          {rows.map((collection) => {
            const previewOffers = collection.offerIds.slice(0, 4).map((id) => offerById.get(id))
            const cover = collection.image || previewOffers.find((offer) => offer?.images[0])?.images[0]
            return (
              <article key={collection.id} className="dash-collection-card">
                <button type="button" className="dash-collection-cover" onClick={() => setEditing(collection)} aria-label={`${t('create.edit')}: ${collection.title}`}>
                  {cover ? <img src={cover} alt="" loading="lazy" /> : <IconCategory size={28} aria-hidden="true" />}
                </button>
                <div className="dash-collection-body">
                  <div className="dash-collection-card-heading">
                    <button type="button" className="dash-collection-title" onClick={() => setEditing(collection)}>{collection.title}</button>
                    <span className={`dash-collection-badge${collection.kind === 'auto' ? ' is-auto' : ''}`}>
                      {collection.kind === 'auto' ? <IconWand size={12} aria-hidden="true" /> : null}
                      {t(`collections.kind.${collection.kind}`)}
                    </span>
                  </div>
                  {collection.description ? <p>{collection.description}</p> : null}
                  <div className="dash-collection-products">
                    <span className="dash-collection-product-images" aria-hidden="true">
                      {previewOffers.map((offer, index) => (
                        <span key={collection.offerIds[index]} title={offer?.title}>
                          {offer?.images[0] ? <img src={offer.images[0]} alt="" loading="lazy" /> : <IconCategory size={15} />}
                        </span>
                      ))}
                      {collection.count > 4 ? <span className="dash-collection-product-more">+{collection.count - 4}</span> : null}
                    </span>
                    <small>{t('collections.count', { count: collection.count })}</small>
                  </div>
                </div>
                <div className="dash-collection-card-actions">
                  <button type="button" className="dash-secondary-btn" onClick={() => setEditing(collection)}>
                    <IconPencil size={14} /> {t('create.edit')}
                  </button>
                  <div className="dash-collection-menu" onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setMenuId(null)
                  }} onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.stopPropagation()
                      setMenuId(null)
                      event.currentTarget.querySelector('button')?.focus()
                    }
                  }}>
                    <button type="button" className="dash-icon-btn" aria-label={t('offers.moreOptions')} aria-expanded={menuId === collection.id} aria-controls={`collection-actions-${collection.id}`} onClick={() => setMenuId(menuId === collection.id ? null : collection.id)}>
                      <IconDots size={18} />
                    </button>
                    {menuId === collection.id ? (
                      <div className="dash-collection-menu-popup" id={`collection-actions-${collection.id}`}>
                        <button type="button" onClick={() => { setMenuId(null); setEditing(collection) }}><IconPencil size={14} /> {t('create.edit')}</button>
                        <a href={designHref(collection)} target="_blank" rel="noreferrer" onClick={() => setMenuId(null)}><IconLayoutGrid size={14} /> {t('collections.designPage')}</a>
                        {siteOrigin ? <a href={collectionUrl(collection)} target="_blank" rel="noreferrer" onClick={() => setMenuId(null)}><IconExternalLink size={14} /> {t('collections.openLink')}</a> : null}
                        {siteOrigin ? <button type="button" onClick={() => { setMenuId(null); void copyLink(collection) }}><IconLink size={14} /> {t('collections.copyLink')}</button> : null}
                        <button type="button" className="dash-collection-remove" onClick={() => { setMenuId(null); setRemoving(collection) }}><IconTrash size={14} /> {t('collections.removeConfirm')}</button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {editing ? (
        <CollectionDialog
          projectId={projectId}
          token={token}
          collection={editing === 'new' ? null : editing}
          offers={offers.data ?? []}
          offerFields={fields.data?.fields ?? {}}
          currency={currency}
          onClose={() => setEditing(null)}
          onSaved={saved}
          /* Una marca o etiqueta eliminada de todo el proyecto cambia ofertas y condiciones guardadas. */
          onLabelRemoved={() => { void offers.refresh(); void collections.refresh() }}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={t('collections.removeTitle', { title: removing.title })}
          message={t('collections.removeBody')}
          confirmLabel={t('collections.removeConfirm')}
          tone="danger"
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await deleteCollection(projectId, removing.id, token)
              collections.setData(rows.filter((row) => row.id !== removing.id))
              setRemoving(null)
            } catch (error) {
              showToast(translateServerError(error, t('error.unexpected')), 'error')
            }
          }}
        />
      ) : null}
    </div>
  )
}
